/* @flow */

import { includes, chain } from 'lodash'

import type { RootState, User, UsersStore, UsersQuery } from '../types'
import {
  upsert,
  parseIntOr,
  EmbedError,
  ActionRequiresPropertyError,
} from '../helpers'
import { selectCustomerById } from './customersStore'
import { selectRoleById } from './rolesStore'

import type { Action } from '../types'

export const INTO_USERS_STORE: string = 'opinary/INTO_USERS_STORE'

export const AUTH_TOKEN = 'auth_token'
export const AUTH_COOKIE = 'auth'
export const OPINARY_USER = 'opinary_user'

export function intoUsersStore(items: Array<User>): Action {
  return {
    type: INTO_USERS_STORE,
    items,
  }
}

function embedAssociated(rootState: RootState, item: User): User {
  const customer = selectCustomerById(rootState, item.customerId)
  if (customer == null) {
    throw new EmbedError(
      { name: 'user', id: item.id },
      { name: 'customer', id: item.customerId }
    )
  }

  const roles = item.roleIds.map(id => {
    const role = selectRoleById(rootState, id)
    if (role == null) {
      throw new EmbedError({ name: 'user', id: item.id }, { name: 'role', id })
    }
    return role
  })

  return Object.assign({}, item, {
    customer,
    roles,
  })
}

export function selectUsersByQuery(
  rootState: RootState,
  query: UsersQuery
): Array<User> {
  let seq = chain(rootState.usersStore).values()

  if (query.search != null) {
    const search = query.search.toLowerCase()
    seq = seq.filter(item => {
      return (
        includes(item.name.toLowerCase(), search) ||
        includes(item.email.toLowerCase(), search)
      )
    })
  }

  const limit = query.perPage
  const offset = query.page * query.perPage

  return seq
    .drop(offset)
    .take(limit)
    .map(item => {
      return embedAssociated(rootState, item)
    })
    .value()
}

export function selectUserById(rootState: RootState, id: number): ?User {
  const item = rootState.usersStore[id.toString()]
  if (item == null) {
    return null
  }

  return embedAssociated(rootState, item)
}

export default function reducer(
  state: UsersStore = {},
  action: Action
): UsersStore {
  switch (action.type) {
    case INTO_USERS_STORE:
      if (!action.items) {
        throw new ActionRequiresPropertyError(INTO_USERS_STORE, 'items')
      } else {
        return upsert(state, action.items)
      }
    default:
      return state
  }
}

export function parseUsersQuery(query: Object): UsersQuery {
  const result = {}

  if (query.search != null && query.search.trim() !== '') {
    result.search = query.search
  }

  result.page = parseIntOr(query.page, 0)
  result.perPage = parseIntOr(query.per_page, 50)

  return result
}

export function isAdmin(user: User): boolean {
  return user ? user.roleIds.indexOf('admin') > -1 : false
}

export function isLibrary(user: User): boolean {
  return user ? user.roleIds.indexOf('library') > -1 : false
}

export function hasAutomationPermissions(user: User): boolean {
  return user ? user.roleIds.indexOf('automation') > -1 : false
}

export function hasReportPermissions(user: User): boolean {
  return user ? user.roleIds.indexOf('report') > -1 : false
}

export function hasPublisherReportPermissions(user: User): boolean {
  return user ? user.roleIds.indexOf('conversions') > -1 : false
}

export function hasRevenueReportPermissions(user: User): boolean {
  return user ? user.roleIds.indexOf('revenue') > -1 : false
}

export function hasAnalyticsPermissions(user: User): boolean {
  return user ? user.roleIds.indexOf('analytics') > -1 : false
}

export function hasDmpPermissions(user: User): boolean {
  return user ? user.roleIds.indexOf('dmp') > -1 : false
}

export function isToolUser(user: ?User): boolean {
  return user ? Boolean(user.toolEnabled) : false
}

export function isToolPollCreatorUser(user: ?User): boolean {
  return user ? Boolean(user.toolPollCreatorEnabled) : false
}



// WEBPACK FOOTER //
// ./src/stores/usersStore.js